/**
 * CANONICAL TIME MATH MODULE
 * 
 * Single source of truth for all scheduling time calculations.
 * Used by:
 * - CSV import (src/pages/ImportJobs.tsx)
 * - Smart assign (supabase/functions/smart-assign/index.ts)
 * - Break adjustment (src/utils/breakAdjustment.ts)
 * - Candidate explorer (supabase/functions/_shared/candidateExplorer.ts)
 * 
 * CRITICAL: All paths MUST use these functions to ensure consistency.
 * 
 * ## Key Concepts (R40 FIX-11)
 * 
 * - **predicted_minutes**: Book/job-catalog minutes summed from ticket_jobs.
 *   Written by client (AddJobDialog, EditJob, DVI) after recalculating from jobs.
 *   GlobalCascade reads this column directly — it does NOT recompute from jobs.
 * 
 * - **opti_time**: Wall-clock scheduling duration = calculateWallMinutes(predicted_minutes, eff, buffer).
 *   Written by server (reassignCore, eodRolloverHandler, globalCascade).
 *   Represents the actual calendar time the ticket occupies on the board.
 * 
 * - **opti_finish**: Wall-clock finish time = start_time + opti_time (adjusted for breaks).
 *   Derived via calculateTicketOptiFinish. Written by cascade engines.
 *   Used for Chronomancer chaining: next ticket starts at prev.opti_finish + 1 min.
 * 
 * Cross-references:
 *   supabase/functions/_shared/reassignCore.ts
 *   supabase/functions/_shared/rolloverChainAuthority.ts (applyChainWorkChange)
 *   supabase/functions/_shared/sequenceNormalizer.ts
 */

// B-W4 (founder decision 2026-10-18): the buffer is resolved by THE planning-buffer rule (unset = 0 %,
// explicit 0 = 0, clamped 0..100, no hidden 25) — the server's definition, re-exported, never restated.
import { resolvePlanningBufferPct } from './planningBuffer';

/**
 * Calculate wall-time duration for scheduling
 * 
 * Formula: wallMinutes = (bookMinutes / effFactor) * (1 + bufferPct/100)
 * Where effFactor = max(efficiencyPct, 1) / 100
 * 
 * Examples:
 * - 60 min @ 160% efficiency with 10% buffer = (60 / 1.6) * 1.10 = 41.25 → 41 min
 * - 60 min @ 100% efficiency with 10% buffer = (60 / 1.0) * 1.10 = 66 min
 * - 60 min @ 80% efficiency with 10% buffer  = (60 / 0.8) * 1.10 = 82.5 → 83 min
 * 
 * @param bookMinutes - Base/book duration from job catalog
 * @param efficiencyPct - Technician efficiency percentage (e.g., 160 for 160%)
 * @param bufferPct - Shop buffer percentage (e.g., 10 for 10%)
 * @returns Wall-time in minutes (rounded)
 */
export function calculateWallMinutes(
  bookMinutes: number,
  efficiencyPct: number,
  bufferPct: number | null | undefined
): number {
  // Normalize efficiency (handle edge cases)
  const effFactor = Math.max(efficiencyPct, 1) / 100;
  
  // Wall time = book time / efficiency
  // Example: 60 min @ 160% efficiency = 60 / 1.6 = 37.5 min
  // Example: 60 min @ 80% efficiency = 60 / 0.8 = 75 min
  const wallMinutes = bookMinutes / effFactor;
  
  // Add buffer (shop slack time)
  const withBuffer = wallMinutes * (1 + resolvePlanningBufferPct(bufferPct) / 100);
  
  return Math.round(withBuffer);
}

/**
 * Inverse of calculateWallMinutes: convert wall-time back to book/predicted minutes.
 * 
 * Formula: bookMinutes = max(1, floor(wallMin * effFactor / bufferFactor))
 * 
 * MIRROR — MUST match supabase/functions/_shared/canonicalTimeMath.ts
 * 
 * @param wallMin - Wall-time minutes available
 * @param effPct - Technician efficiency percentage (e.g., 160 for 160%)
 * @param bufPct - Shop buffer percentage (e.g., 10 for 10%)
 * @returns Book/predicted minutes (floored)
 */
export function wallToBookMinutes(wallMin: number, effPct: number, bufPct: number | null | undefined): number {
  const effFactor = Math.max(effPct, 1) / 100;
  const bufferFactor = 1 + resolvePlanningBufferPct(bufPct) / 100;
  return Math.max(1, Math.floor(wallMin * effFactor / bufferFactor));
}

/**
 * Calculate when a ticket finishes, accounting for breaks
 * 
 * Iteratively calculates finish time by detecting break overlaps and pushing finish time forward.
 * 
 * @param startMin - Ticket start time in minutes from midnight
 * @param durationMin - Ticket wall-time duration
 * @param breaks - Array of break windows (in minutes from midnight)
 * @returns Finish time in minutes from midnight (including break overlap)
 */
export function calculateTicketOptiFinish(
  startMin: number,
  durationMin: number,
  breaks: Array<{ start: number; end: number }>
): number {
  let finishMin = startMin + durationMin;
  let breakOverlap = 0;
  let previousOverlap = -1;
  
  // Iteratively calculate break overlap
  // Keep looping until overlap stabilizes (handles multi-break scenarios)
  while (previousOverlap !== breakOverlap) {
    previousOverlap = breakOverlap;
    breakOverlap = 0;
    
    for (const brk of breaks) {
      const overlap = Math.max(
        0,
        Math.min(finishMin, brk.end) - Math.max(startMin, brk.start)
      );
      breakOverlap += overlap;
    }
    
    finishMin = startMin + durationMin + breakOverlap;
  }
  
  return finishMin;
}

/**
 * Parse time string to minutes from midnight
 * 
 * @param timeStr - Time in HH:MM format (e.g., "14:30")
 * @returns Minutes from midnight (e.g., 870 for 14:30)
 */
export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return NaN;
  const [h, m] = String(timeStr).slice(0, 5).split(':');
  return Number(h) * 60 + Number(m || 0);
}

/**
 * Format minutes from midnight to HH:MM string
 * 
 * @param min - Minutes from midnight
 * @returns Time in HH:MM format
 */
export function formatMinutesToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
